/**
 * Menu Builder Utilities - Functions for building and manipulating menu structures
 * Created: 1405/02/17
 */

import { MenuItem, MergedMenuItem } from '../types';

/**
 * Build tree structure from flat list of menu items
 */
export const buildMenuTree = (items: MenuItem[]): MergedMenuItem[] => {
  const itemMap = new Map<string, MergedMenuItem>();
  const rootItems: MergedMenuItem[] = [];
  
  // First pass: create map
  items.forEach(item => {
    if (item.isVisible && item.isActive) {
      itemMap.set(item.id, { ...item, children: [], level: 0 });
    }
  });
  
  // Second pass: build relationships
  items.forEach(item => {
    if (!item.isVisible || !item.isActive) return;
    
    const mergedItem = itemMap.get(item.id)!;
    
    if (item.parentId && itemMap.has(item.parentId)) {
      const parent = itemMap.get(item.parentId)!;
      parent.children = parent.children || [];
      parent.children.push(mergedItem);
      mergedItem.level = parent.level + 1;
    } else {
      rootItems.push(mergedItem);
    }
  });
  
  // Sort by order at each level
  const sortByOrder = (items: MergedMenuItem[]) => {
    items.sort((a, b) => a.order - b.order);
    items.forEach(item => {
      if (item.children && item.children.length > 0) {
        sortByOrder(item.children);
      }
    });
  };
  
  sortByOrder(rootItems);
  return rootItems;
};

/**
 * Flatten tree structure back to flat list
 */
export const flattenMenuTree = (tree: MergedMenuItem[]): MenuItem[] => {
  const flat: MenuItem[] = [];
  
  const flatten = (items: MergedMenuItem[]) => {
    items.forEach(item => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { children, level, ...menuItem } = item;
      flat.push(menuItem);
      if (children && children.length > 0) {
        flatten(children);
      }
    });
  };
  
  flatten(tree);
  return flat;
};

/**
 * Find item by ID in tree
 */
export const findItemInTree = (
  tree: MergedMenuItem[], 
  id: string
): MergedMenuItem | undefined => {
  for (const item of tree) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findItemInTree(item.children, id);
      if (found) return found;
    }
  }
  return undefined;
};

/**
 * Find parent of an item
 */
export const findParentInTree = (
  tree: MergedMenuItem[], 
  childId: string
): MergedMenuItem | undefined => {
  for (const item of tree) {
    if (item.children?.some(child => child.id === childId)) {
      return item;
    }
    if (item.children) {
      const found = findParentInTree(item.children, childId);
      if (found) return found;
    }
  }
  return undefined;
};

/**
 * Get all descendants of an item
 */
export const getDescendants = (item: MergedMenuItem): string[] => {
  const ids: string[] = [item.id];
  
  if (item.children) {
    item.children.forEach(child => {
      ids.push(...getDescendants(child));
    });
  }
  
  return ids;
};

/**
 * Get depth of item in tree
 */
export const getItemDepth = (tree: MergedMenuItem[], itemId: string): number => {
  const item = findItemInTree(tree, itemId);
  return item?.level ?? 0;
};

/**
 * Reorder items within same parent
 */
export const reorderItems = (
  items: MenuItem[],
  itemId: string,
  newIndex: number
): MenuItem[] => {
  const item = items.find(i => i.id === itemId);
  if (!item) return items;
  
  const siblings = items.filter(i => i.parentId === item.parentId);
  const otherItems = items.filter(i => i.parentId !== item.parentId);
  
  // Remove item from siblings
  const filteredSiblings = siblings.filter(i => i.id !== itemId);
  
  // Insert at new position
  filteredSiblings.splice(newIndex, 0, item);
  
  // Update order numbers
  filteredSiblings.forEach((sibling, index) => {
    sibling.order = index + 1;
  });
  
  return [...otherItems, ...filteredSiblings];
};

/**
 * Move item to new parent
 */
export const moveItemToParent = (
  items: MenuItem[],
  itemId: string,
  newParentId: string | null
): MenuItem[] => {
  return items.map(item => {
    if (item.id === itemId) {
      return { ...item, parentId: newParentId };
    }
    return item;
  });
};

/**
 * Get siblings of an item
 */
export const getSiblings = (items: MenuItem[], itemId: string): MenuItem[] => {
  const item = items.find(i => i.id === itemId);
  if (!item) return [];
  
  return items.filter(i => i.parentId === item.parentId && i.id !== itemId);
};
